import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { toast } from "sonner";

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  templateName: string;
  children: React.ReactNode;
  onExportPDF: () => Promise<void>;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function PreviewModal({
  open,
  onClose,
  templateName,
  children,
  onExportPDF,
  currentPage,
  totalPages,
  onPageChange,
}: Readonly<PreviewModalProps>) {
  const [zoom] = useState(100);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await onExportPDF();
      toast.success("Preview PDF downloaded");
    } catch (error) {
      toast.error("PDF generation failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      handlePrevPage();
    } else if (e.key === "ArrowRight") {
      handleNextPage();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-[100vw] max-h-[100vh] w-full h-full p-0 gap-0"
        onKeyDown={handleKeyDown}
      >
        {/* Top Bar */}
        <div className="h-14 border-b flex items-center justify-between px-6 bg-white">
          <Button variant="ghost" onClick={onClose} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Editor
          </Button>

          <div className="flex items-center gap-2">
            <span className="font-medium">{templateName}</span>
            <Badge variant="secondary" className="bg-teal-50 text-teal-700 border-teal-200">
              Customer Statement
            </Badge>
          </div>

          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </>
            )}
          </Button>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 bg-[#f0f0f0] overflow-y-auto relative">
          <div className="min-h-full flex items-start justify-center py-10">
            <div style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center' }}>
              {children}
            </div>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}
